import { Line, LineChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { TableCell, TableRow } from "@/components/ui/table";
import { downloadCsv, toCsv } from "@/lib/utils/csv";
import { DashboardTable, useDashboardTable, type DashboardTableColumn } from "./dashboard-table";
import { useDashboardUrlState } from "./useDashboardUrlState";
import { referralPeriods, periodLabel } from "./referral-periods";

type VolumeRow = { period: string; count: number };

export function ReferralVolume({ id, title, rows, from, to, granularity, isLoading, error, note }: {
  id: string; title: string; rows: VolumeRow[]; from: string; to: string; granularity: string;
  isLoading: boolean; error?: string; note?: string;
}) {
  const modeKey = `${id}Mode`;
  // The page hydrates all per-chart defaults together, avoiding competing mounts.
  const [state, update] = useDashboardUrlState({ [modeKey]: "" });
  const mode = state[modeKey] || (id === "vervet-code" ? "table" : "chart");
  const periods = referralPeriods(from, to, granularity);
  const byPeriod = new Map(rows.map((r) => [r.period, Number(r.count)]));
  const data = periods.map((period) => ({ period, count: byPeriod.get(period) ?? 0 }));
  const columns: DashboardTableColumn<VolumeRow>[] = [
    { id: "period", label: periodLabel(granularity), value: (r) => r.period },
    { id: "count", label: "Kunder", value: (r) => r.count, numeric: true, align: "right" },
  ];
  const table = useDashboardTable(id, columns, data);
  const total = rows.reduce((sum, r) => sum + Number(r.count), 0);
  const exportCsv = () => downloadCsv(toCsv(
    [periodLabel(granularity), "Kunder"],
    (mode === "table" ? table.viewRows : data).map((r) => [r.period, r.count]), ";",
  ), `${id}-${from}-${to}.csv`, { bom: true });
  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <CardTitle>{title}<span className="ml-2 text-sm font-normal text-admin-text-subtle">{!isLoading && !error ? `${total} totalt` : ""}</span></CardTitle>
        <div className="flex items-end gap-2">
          <label className="space-y-1"><span className="text-xs text-admin-text-subtle">Visning</span>
            <Select value={mode} onChange={(e) => update({ [modeKey]: e.target.value })}><option value="chart">Graf</option><option value="table">Tabell</option></Select>
          </label>
          <Button size="sm" variant="outline" disabled={!rows.length || Boolean(error) || isLoading} onClick={exportCsv}><Download className="h-3 w-3" />Eksporter CSV</Button>
        </div>
      </CardHeader>
      <CardContent>
        {note && <p className="mb-3 text-xs text-admin-text-subtle">{note}</p>}
        {isLoading ? <p className="py-8 text-sm text-admin-text-subtle">Laster…</p>
          : error ? <p role="alert" className="py-8 text-sm text-admin-danger-400">{error}</p>
          : !rows.length ? <p className="py-8 text-sm text-admin-text-subtle">Ingen kunder med valgte filtre i perioden.</p>
          : mode === "table" ? <DashboardTable table={table} columns={columns} renderRow={(r) => <TableRow key={r.period}><TableCell>{r.period}</TableCell><TableCell className="text-right tabular-nums">{r.count}</TableCell></TableRow>} />
          : <div className="overflow-x-auto"><div style={{ minWidth: data.length * 42 }}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data} margin={{ top: 26, right: 20, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-admin-card-border)" />
                <XAxis dataKey="period" tick={{ fontSize: 11, fill: "var(--color-admin-text-subtle)" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "var(--color-admin-text-subtle)" }} />
                <Tooltip contentStyle={{ backgroundColor: "var(--color-admin-card)", borderColor: "var(--color-admin-card-border)", color: "var(--color-admin-text-primary)" }} />
                <Line dataKey="count" name={id === "referrals" ? "Vervinger" : "Kunder uten kjent verver"} stroke={id === "referrals" ? "var(--color-admin-info-400)" : "var(--color-admin-mint-500)"} strokeWidth={2} dot={data.length > 60 ? false : { r: 3 }} isAnimationActive={false}>
                  <LabelList dataKey="count" position="top" fill="var(--color-admin-text-primary)" fontSize={11} formatter={(v) => Number(v) > 0 ? String(v) : ""} />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          </div></div>}
      </CardContent>
    </Card>
  );
}
